import { ErrorHandler, inject, Injectable, signal } from '@angular/core';
import { catchError, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { StaticPageService } from '../../../page/static-page/static-page.service';
import { LocalHistoryService } from '../local-history.service';
import { SeoService } from '../seo.service';
import { ResponseHandlerService } from '../response-handler.service';
import { combineLatest, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { ILoyaltyLevel } from './loyalty.interface';
import { BonusStage } from '../user/data/user-bonuses.data';
import { OffersService } from '../offers.service';
import { UserBonusesService } from '../user/user-bonuses.service';
import { UserService } from '../user/user.service';
import { ArrayService } from '../array.service';
import { UserCompPointsService } from '../user/user-comp-points.service';
import { GamesService } from '../games/games.service';
import { TranslationService } from '../../shared/translation/translation.service';
import { GroupsService, UserGroup } from '../groups.service';
import { GoogleTagManagerService } from '../google-tag-manager.service';
import { Router } from '@angular/router';
import { ArabicService } from '../arabic.service';
import { GamesLauncherService } from '../games/games-launcher.service';
import { ResolveError } from '../../shared/errors/resolve-error';

@Injectable({
  providedIn: 'root'
})

export class LoyaltyService {
  private _pages = inject(StaticPageService);
  private _history = inject(LocalHistoryService);
  private _seo = inject(SeoService);
  private _responseHandler = inject(ResponseHandlerService);
  private _offers = inject(OffersService);
  private _bonuses = inject(UserBonusesService);
  private _user = inject(UserService);
  private _array = inject(ArrayService);
  private _compPoints = inject(UserCompPointsService);
  private _games = inject(GamesService);
  private _translate = inject(TranslationService);
  private _gtm = inject(GoogleTagManagerService);
  private _groups = inject(GroupsService);
  private _router = inject(Router);
  private _rtl = inject(ArabicService);
  private _gamesLauncher = inject(GamesLauncherService);
  private _errorHandler = inject(ErrorHandler);

  public loaded = signal(false);
  /**
   * Loyalty levels from CMS Observable
   * @private
   */
  private _loyaltyLevels$: ReplaySubject<ILoyaltyLevel[]> = this.resolveLevels$();

  /**
   * Current loyalty level
   * @private
   */
  private _currentLoyaltyLevel: ILoyaltyLevel;

  /**
   * Current loyalty level
   * @private
   */
  public currentLoyaltyLevel$: Observable<ILoyaltyLevel> = new Observable<ILoyaltyLevel>;

  private _imgPath: string = '/assets/img/loyalty';

  private _activeLevelIndex: number = 0;

  public loyaltyPathChoose$: Subject<any> = new Subject<any>();

  get loyaltyLevels$(): ReplaySubject<ILoyaltyLevel[]> {
    return this._loyaltyLevels$;
  }

  get currentLoyaltyLevel(): ILoyaltyLevel {
    return this._currentLoyaltyLevel;
  }

  get Group() {
    return UserGroup;
  }

  get activeLevelIndex(): number {
    return this._activeLevelIndex;
  }

  /**
   * Get slug from URL Params
   *
   * @private
   */
  public resolvePage$() {
    return this._pages.item({slug: 'loyalty-program-redesign'}).pipe(
      filter(data => !!data),
      map(page => {
        if (!page) {
          return this._history.skipHistoryNavigate('/404');
        }

        if (!page.length) {
          return this._history.skipHistoryNavigate('/404');
        } else {
          if (page[0] && !page[0].statusCode) {
            this._seo.setMetaTags(page[0] || {});
            return page[0];
          } else {
            return this._responseHandler.handleResponseStatus(page[0].statusCode);
          }
        }
      })
    );
  }

  /**
   * Click on locked link
   * @param event
   * @param game
   */
  public onLockedTextClick(event: any, game: any) {
    event.preventDefault();
    const target: HTMLElement = event?.target;
    if (target?.tagName === 'A') {
      this.openGame(game, this._rtl.isAr());
    }
  }

  public openGame(game, toGamesIfArabic = false) {
    if (!this._user.auth) {
      this._user.authUser().then();
      return;
    }

    if (this._user?.currentCurrency?.amount < 1) {
      this._router.navigateByUrl('/deposit').then();
      return;
    }

    if (toGamesIfArabic || !game) {
      this._router.navigateByUrl('/games').then();
    } else {
      this._gamesLauncher.openGameByExternalId(game);
    }
  }

  public resolveLevels$(): ReplaySubject<any> {
    return this._user.auth$.pipe(
      switchMap(auth => !!auth ? this._bonuses.freeSpinsList() : of(auth)),
      switchMap(data => {
        return combineLatest([
          typeof data === 'boolean' ? of([]) : this._compPoints.getCompPoints(),
          typeof data === 'boolean' ? of([]) : this._bonuses.loyaltyFreeSpinsBonuses$,
          this._offers.list({category_slug: 'loyalty-levels'}, {gameList: 'gameList'})
        ]);
      }),
      filter(([points, fs, levels]: any) => levels && levels.length),
      map(([points, fs, levels]) => {
        try {
          const generalLevels = levels.filter(l => !l?.slug?.includes('ninja') && !l?.slug?.includes('samurai'));
          const ninjaLevels   = levels.filter(l =>  l?.slug?.includes('ninja'));
          const samuraiLevels = levels.filter(l =>  l?.slug?.includes('samurai'));
          const filteredLevels = this._groups.loyaltyGroup === UserGroup.NINJA
            ? [...generalLevels, ...ninjaLevels]
            : [...generalLevels, ...samuraiLevels];

          this._activeLevelIndex = filteredLevels?.findIndex(this._getActiveIndex.bind(this));
          this._activeLevelIndex = this._activeLevelIndex < 0 ? 0 : this._activeLevelIndex;

          const resolvedLevels = filteredLevels.map((level, i, arr) => {
            const lvl = this._resolveLoyaltyLevel(level, i, fs, this._activeLevelIndex, arr.length, arr, points);
            return {
              ...lvl,
              Bonuses: (lvl?.Bonuses ?? []).filter(b => !!b && typeof b === 'object')
            };
          });

          if (this._user.auth) {
            this._currentLoyaltyLevel = resolvedLevels[this._activeLevelIndex];

            const allTaken = (this._currentLoyaltyLevel?.Bonuses ?? []).every(b => b?.taken === true);
            if (this.currentLoyaltyLevel?.taken || allTaken) {
              this._activeLevelIndex = Math.min(this._activeLevelIndex + 1, resolvedLevels.length - 1);
            }

            const bonuses = (resolvedLevels[this._activeLevelIndex]?.Bonuses ?? []);
            const currentBonus = bonuses.find(
              (b): b is typeof bonuses[number] => !!b && !b.active && !b.taken
            );

            const currLvlPoints = Number(this._currentLoyaltyLevel?.Points ?? 0);
            const bonusPoints   = Number(currentBonus?.points ?? 0);

            const diff = bonusPoints - currLvlPoints;
            const totalPoints = diff > 0 ? diff : bonusPoints;

            this._currentLoyaltyLevel = {
              ...this._currentLoyaltyLevel,
              nextLockedBonus: {
                bonus: currentBonus,
                leftPoints: Math.abs((this._compPoints?.points?.persistent ?? 0) - bonusPoints),
                totalPoints,
                index: Math.min(this._activeLevelIndex + 1, resolvedLevels.length)
              }
            };
          }

          this.currentLoyaltyLevel$ = of(this._currentLoyaltyLevel || null);

          return resolvedLevels;
        } catch (error) {
          this._errorHandler.handleError(new ResolveError(error, 'resolveLevels$'));
          return [];
        }
      }),
      catchError(error => of([])),
      shareReplay(1)
    ) as ReplaySubject<any>;
  }

  public resolveCurrentLevel(cmsLevels: any[]) {
    return cmsLevels.find(level =>
      this._user.info.statuses.some(status => status.id.toLowerCase().trim() ===  level?.BOIdentifier?.trim()?.toLowerCase()))
  }

  /**
   * Resolve loyalty level
   * @param level
   * @param index
   * @param fs
   * @param activeLevelIndex
   * @param levelsArr
   * @param points
   * @private
   */
  private _resolveLoyaltyLevel(
    level: ILoyaltyLevel,
    index: number, fs: any[], activeLevelIndex: number, levelsLength: number, levelsArr: ILoyaltyLevel[], points: any
  ) {
    let bonuses = new Array(Object.values(level.Bonuses).length > 1 ? 9 : 0).fill(null);
    const pts = points.persistent;
    const chargeable = points?.chargeable || 0;
    const total = Number(level?.Points);

    Object.values(level.Bonuses).forEach((bonus, i, arr) => {
      const boIdentifiers = (bonus?.BOIdentifier?.includes(',') ? bonus?.BOIdentifier?.split(',') : [bonus?.BOIdentifier]).map(f => f?.trim());
      const previousBoIdentifiers = (arr[i - 1]?.BOIdentifier?.includes(',') ? arr[i - 1]?.BOIdentifier?.split(',') : [arr[i - 1]?.BOIdentifier]).map(f => f?.trim());
      const freeSpinsForBonus = fs.find(item => boIdentifiers?.includes(item.group_key));
      const previousFreeSpins = fs.find(item => previousBoIdentifiers?.includes(item.group_key));
      const active = fs.some(f => boIdentifiers?.includes(f.group_key) && f.stage === BonusStage.ISSUED);
      const taken = index < activeLevelIndex || fs.some(f => boIdentifiers?.includes(f.group_key) && f.stage === BonusStage.ACTIVATED) || +bonus.points <= pts;
      const isPreviousActivated = previousFreeSpins?.state === 'activated' || arr.length === 1 && index === activeLevelIndex && index !== 0;
      const shortPrize = bonus?.prize?.split(' ')?.find(v => typeof Number(v) === 'number');
      const prizeCashAndFs =  bonus?.prize?.replace(/<br>/g, ' ')?.replace(/^(.*?\d+\s*\+\s*\d+).*$/, '$1');
      const pointsToUnlock = this._user.auth ? Number(bonus.points) - Number(this._compPoints.points.persistent) : Number(bonus.points);
      const unlockMessage = this._translate.translate('t.points-to-unlock', {
        points: pointsToUnlock,
        link: `<a class="link ">${this._translate.translate('t.earn')}</a>`,
        img: `<img src="/assets/img/loyalty/points-img.svg" alt="">`
      });

      const unlockMessageAccount = this._translate.translate('t.unlock-freespins', {
        points: pointsToUnlock,
        fs: shortPrize?.replace('+', '')
      });

      const resolvedBonus = {
        ...bonus,
        active, taken, isPreviousActivated, unlockMessage, shortPrize, unlockMessageAccount, prizeCashAndFs,
        activateId: freeSpinsForBonus?.stage === BonusStage.ISSUED ? freeSpinsForBonus?.id : null,
        expired: freeSpinsForBonus?.stage === 'expired',
        games: this._resolveGamesForSublevel(level?.gameList, bonus?.game),
        smallImg: this._getBonusImage('small', index, i, levelsLength, arr.length, taken),
        bigImg: this._getBonusImage('big', index, i, levelsLength, arr.length, taken),
      };

      bonuses[i] = resolvedBonus;

      // One bonus for Vip bonus. If bonus taken
      if (index === levelsLength - 1 && taken) {
        bonuses = bonuses.slice(0, 1);
      }
    });

    const activeIndexBonus = bonuses.findIndex(b => b?.active || b?.isPreviousActivated);

    let resolvedLevel: ILoyaltyLevel = {
      ...level,
      Bonuses: bonuses,
      chargeable,
      pts,
      left: total - pts,
      index: index + 1,
      levelsLength,
      pointsFrom: +level.Points,
      pointsTo: +levelsArr[index + 1]?.Points || Infinity,
      active: this._initActiveLevel(level),
      taken: index < activeLevelIndex || (activeLevelIndex === levelsLength - 1 && bonuses?.every(b => b === null || b?.taken || b?.expired)),
      choosenBonus: {bonus: bonuses[activeIndexBonus >= 0 ? activeIndexBonus : 0], index: activeIndexBonus >= 0 ? activeIndexBonus : 0}
    };

    return resolvedLevel;
  }

  private _getBonusImage(
    imgType: 'small' | 'big',
    levelIndex: number,
    bonusIndex: number,
    levelsLength: number,
    bonusesLength: number,
    taken: boolean
  ): string {
    return this._imgPath + (
      taken && levelIndex === levelsLength - 1 ?
        `/sublevels/${levelIndex}/${imgType}${this._rtl.isAr() ? '/ar/' : '/'}vip.png` :
        `/sublevels/${levelIndex}/${imgType}/${this._rtl.isAr() ? 'ar/' : ''}${bonusIndex}.png`
    );
  }
  /**
   * Return true or false for set active and isSliderMode fields
   * @private
   */
  private _initActiveLevel(level: ILoyaltyLevel): boolean {
    return this._user.auth ?
      level?.BOIdentifier?.trim()?.toLowerCase() === this._user?.loyaltyActiveLevel?.toLowerCase()?.trim() :
      false;
  }

  /**
   * Return active level index
   * @param level
   * @private
   */
  private _getActiveIndex(level: ILoyaltyLevel): boolean {
    return this._user.auth ?
      level?.BOIdentifier?.trim()?.toLowerCase() === this._user.loyaltyActiveLevel?.toLowerCase()?.trim() :
      false;
  }

  /**
   * Ad signed in player to group
   *
   * @param group
   */
  public addPlayerToGroup(group: UserGroup) {
    if (group === UserGroup.SAMURAI) {
      this._gtm.loyaltyPageClick('register_loyalty_samurai', 'loyalty_samurai');
    } else {
      this._gtm.loyaltyPageClick('register_loyalty_ninja', 'loyalty_ninja');
    }

    if (this._user.auth && !this._groups.loyaltyGroup) {
      this._groups.addToGroup(group).pipe(
        tap(() => this.loyaltyPathChoose$.next(true)),
        switchMap(() => this._user.getUserInfo()),
        tap(() => {
          this._router.navigateByUrl('/deposit').then();
        })
      ).subscribe();
    }
  }

  private _resolveGamesForSublevel(gameList: any[], gamesForSublevel: string): any[] {
    const bonusGamesSlugArr = gamesForSublevel?.includes(',') ? gamesForSublevel.split(',').map(g => g?.trim()) : [gamesForSublevel];
    if (Array.isArray(gameList) || (gameList && (Array.isArray(Object.values(gameList))))) {
      const gamesList = Array.isArray(gameList) ? gameList : Object.values(gameList);
      const games = bonusGamesSlugArr?.map(gameExternalId => gamesList?.find(game => game?.externalId === gameExternalId));
      return games.filter(game => !!game).map(g => ({...g, imgSrc: this._games.resolveImageSrc(g)}));
    }
    return [];
  }
}
